/**
 * ============================================================
 * DIBAI 18K — Header Principal
 * Asset: slider-dibai.js
 * Versión: 1.0.0
 *
 * Funcionalidades:
 * - Drawer mobile (apertura, cierre, submenús, foco, ESC)
 * - Modal de búsqueda (apertura, cierre, foco, ESC)
 * - Bloqueo de scroll con compensación de scrollbar
 * - Foco atrapado en drawer y modal (a11y)
 * - Navegación por teclado en menú desktop
 * ============================================================
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────
  // CONSTANTES
  // ─────────────────────────────────────────
  const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  // ─────────────────────────────────────────
  // UTILIDADES
  // ─────────────────────────────────────────

  /**
   * Obtener el ancho del scrollbar para compensar al bloquear scroll.
   * @returns {number}
   */
  function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  /**
   * Bloquear el scroll del body con compensación de scrollbar.
   */
  function lockScroll() {
    const sbWidth = getScrollbarWidth();
    document.documentElement.style.setProperty('--scrollbar-width', `${sbWidth}px`);
    document.body.classList.add('dbh-scroll-locked');
  }

  /**
   * Desbloquear el scroll del body.
   */
  function unlockScroll() {
    document.body.classList.remove('dbh-scroll-locked');
    document.documentElement.style.removeProperty('--scrollbar-width');
  }

  /**
   * Obtener todos los elementos enfocables dentro de un contenedor.
   * @param {Element} container
   * @returns {Element[]}
   */
  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
      (el) => !el.closest('[hidden]') && !el.closest('[disabled]')
    );
  }

  /**
   * Atrapar el foco dentro de un contenedor (trap focus).
   * @param {KeyboardEvent} event
   * @param {Element} container
   */
  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  // ─────────────────────────────────────────
  // CLASE HEADER DIBAI
  // ─────────────────────────────────────────
  class HeaderDibai {
    /**
     * @param {string} sectionId - ID de la sección Shopify
     */
    constructor(sectionId) {
      this.id = sectionId;
      this.root = document.getElementById(`slider-dibai-${sectionId}`);

      if (!this.root) return;

      // Elementos del drawer
      this.drawerEl   = document.getElementById(`dbh-drawer-${sectionId}`);
      this.hamburger  = this.root.querySelector('[data-dbh-toggle="drawer"]');
      this.drawerPanel = this.drawerEl?.querySelector('.dbh-drawer__panel');

      // Elementos del search modal
      this.searchModal   = document.getElementById(`dbh-search-modal-${sectionId}`);
      this.searchInput   = this.searchModal?.querySelector('[data-dbh-search-input]');
      this.searchResults = this.searchModal?.querySelector('[data-dbh-search-results]');
      this.searchClear   = this.searchModal?.querySelector('[data-dbh-search-clear]');

      // Estado
      this._drawerOpen    = false;
      this._searchOpen    = false;
      this._lastFocusedEl = null;
      this._searchQuery   = '';
      this._searchTimer   = null;
      this._activeIndex   = -1;
      this._resultItems   = [];

      // Manejadores de eventos (bound)
      this._onKeydown      = this._handleKeydown.bind(this);
      this._onSearchInput  = this._handleSearchInput.bind(this);
      this._onSearchKeyNav = this._handleSearchKeyNav.bind(this);

      this._init();
    }

    // ─────────────────────────────────
    // INICIALIZACIÓN
    // ─────────────────────────────────
    _init() {
      this._bindToggleButtons();
      this._bindCloseButtons();
      this._bindSubmenus();
      this._bindOverlays();
      this._bindDesktopNav();
      this._bindScrollShadow();
    }

    // Header fijo + sombra + autohide al hacer scroll
    _bindScrollShadow() {
      const el = this.root;
      if (!el) return;

      // Horizon en desktop usa .page-wrapper con overflow-y:auto y height:100dvh
      // El scroll ocurre DENTRO del page-wrapper, no en window.
      // Por eso position:sticky no funciona y window.scrollY siempre es 0.
      // Solución: forzar position:fixed y escuchar scroll en .page-wrapper.
      this._enforceFixedHeader(el);

      const isAutohide = el.classList.contains('dbh-root--autohide');
      const scrollTarget = document.querySelector('.page-wrapper') || window;

      let lastScrollTop = 0;
      let ticking = false;

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const scrollTop = scrollTarget === window
            ? window.scrollY
            : scrollTarget.scrollTop;

          // Sombra al hacer scroll
          el.classList.toggle('is-scrolled', scrollTop > 8);

          // Autohide: solo en desktop (≥750px)
          if (isAutohide && window.innerWidth >= 750) {
            const delta = scrollTop - lastScrollTop;
            if (delta > 4 && scrollTop > 80) {
              // Scrolleando hacia abajo → ocultar
              el.classList.add('is-hidden');
            } else if (delta < -4) {
              // Scrolleando hacia arriba → mostrar
              el.classList.remove('is-hidden');
            }
          } else {
            el.classList.remove('is-hidden');
          }

          lastScrollTop = Math.max(0, scrollTop);
          ticking = false;
        });
      };

      scrollTarget.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      // Reactivar en resize (por si cambia de mobile a desktop)
      window.addEventListener('resize', () => { el.classList.remove('is-hidden'); }, { passive: true });
      onScroll();
    }

    // Forzar header fijo con position:fixed

    // ─────────────────────────────────────────
    // AUTOCOMPLETE / BÚSQUEDA PREDICTIVA
    // ─────────────────────────────────────────

    _handleSearchInput(e) {
      const query = e.target.value.trim();
      this._searchQuery = query;

      // Mostrar/ocultar botón limpiar
      if (this.searchClear) {
        this.searchClear.hidden = query.length === 0;
      }

      if (query.length < 2) {
        this._hideResults();
        return;
      }

      // Debounce 280ms
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this._fetchResults(query);
      }, 280);
    }

    _handleSearchKeyNav(e) {
      if (!this.searchResults || this.searchResults.hidden) return;

      const items = this._resultItems;
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this._activeIndex = Math.min(this._activeIndex + 1, items.length - 1);
        this._updateActiveItem();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this._activeIndex = Math.max(this._activeIndex - 1, -1);
        this._updateActiveItem();
      } else if (e.key === 'Enter' && this._activeIndex >= 0) {
        e.preventDefault();
        const activeItem = items[this._activeIndex];
        const url = activeItem?.dataset.dbhResultUrl;
        if (url) window.location.href = url;
      } else if (e.key === 'Escape') {
        this._hideResults();
      }
    }

    _updateActiveItem() {
      this._resultItems.forEach((item, idx) => {
        const active = idx === this._activeIndex;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          item.scrollIntoView({ block: 'nearest' });
          // Actualizar input con título del producto seleccionado
          const title = item.querySelector('.dbh-search-result-item__title');
          if (title) this.searchInput.value = title.textContent;
        }
      });
    }

    async _fetchResults(query) {
      // Mostrar spinner
      this._showLoading();

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
        if (!resp.ok) throw new Error('Search error');

        const data = await resp.json();
        const products = data?.resources?.results?.products || [];

        this._renderResults(products, query);
      } catch (err) {
        console.warn('[DBH Search]', err);
        this._hideResults();
      }
    }

    _renderResults(products, query) {
      if (!this.searchResults) return;

      this._activeIndex = -1;

      if (!products.length) {
        this.searchResults.innerHTML = `
          <p class="dbh-search-results__empty">
            No se encontraron resultados para <strong>${this._escHtml(query)}</strong>
          </p>`;
        this.searchResults.removeAttribute('hidden');
        this._resultItems = [];
        return;
      }

      const searchUrl = `/search?type=product&q=${encodeURIComponent(query)}`;

      const listItems = products.map((p, i) => {
        const imgTag = p.featured_image?.url
          ? `<img class="dbh-search-result-item__img" src="${p.featured_image.url}&width=120" alt="${this._escHtml(p.title)}" loading="lazy" decoding="async" width="56" height="56">`
          : `<div class="dbh-search-result-item__no-img">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5" opacity="0.4"/></svg>
             </div>`;

        const titleHL = this._highlight(p.title, query);

        const price = p.price ? this._formatPrice(p.price) : '';
        const comparePrice = (p.compare_at_price && p.compare_at_price > p.price)
          ? `<span class="dbh-search-result-item__price--compare">${this._formatPrice(p.compare_at_price)}</span>`
          : '';

        return `
          <li role="option" aria-selected="false">
            <a
              class="dbh-search-result-item"
              href="${p.url}"
              data-dbh-result-url="${p.url}"
              tabindex="-1"
            >
              <div class="dbh-search-result-item__img-wrap">${imgTag}</div>
              <div class="dbh-search-result-item__info">
                <span class="dbh-search-result-item__title">${titleHL}</span>
                ${price ? `<span class="dbh-search-result-item__price">${price}${comparePrice}</span>` : ''}
              </div>
              <svg class="dbh-search-result-item__arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M4 7h6M7 4l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </li>`;
      }).join('');

      this.searchResults.innerHTML = `
        <p class="dbh-search-results__section-title">Productos</p>
        <ul class="dbh-search-results__list" role="listbox">
          ${listItems}
        </ul>
        <div class="dbh-search-results__footer">
          <a href="${searchUrl}" class="dbh-search-results__view-all">
            Ver todos los resultados
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>`;

      this.searchResults.removeAttribute('hidden');

      // Guardar items para navegación con teclado
      this._resultItems = Array.from(
        this.searchResults.querySelectorAll('.dbh-search-result-item')
      );
    }

    _showLoading() {
      if (!this.searchResults) return;
      this.searchResults.innerHTML = `
        <div class="dbh-search-results__loading">
          <div class="dbh-search-results__spinner"></div>
          Buscando...
        </div>`;
      this.searchResults.removeAttribute('hidden');
    }

    _hideResults() {
      if (this.searchResults) this.searchResults.setAttribute('hidden', '');
      this._resultItems = [];
      this._activeIndex = -1;
    }

    _clearSearch() {
      if (this.searchInput) this.searchInput.value = '';
      if (this.searchClear) this.searchClear.hidden = true;
      this._hideResults();
      this._searchQuery = '';
      clearTimeout(this._searchTimer);
    }

    _highlight(text, query) {
      const safe = this._escHtml(text);
      const safeQ = this._escHtml(query);
      const re = new RegExp(`(${safeQ.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return safe.replace(re, '<mark>$1</mark>');
    }

    _formatPrice(price) {
      // La API /search/suggest.json devuelve el precio como número entero en pesos (COP)
      // COP no tiene subunidades — 1994800 = $1.994.800
      const num = parseInt(price, 10);
      if (isNaN(num)) return String(price);
      return '$ ' + num.toLocaleString('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }

    _escHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _enforceFixedHeader(el) {
      // Ya está fixed via CSS si sticky_header está activo
      // Pero necesitamos override porque el page-wrapper bloquea sticky
      el.style.cssText += `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        z-index: 50 !important;
      `;

      // Spacer para que el contenido no quede bajo el header fijo
      const existingSpacer = document.getElementById('dbh-spacer-' + this.sectionId);
      if (existingSpacer) return;

      const spacer = document.createElement('div');
      spacer.id = 'dbh-spacer-' + this.sectionId;
      spacer.setAttribute('aria-hidden', 'true');

      const updateHeight = () => {
        spacer.style.height = el.offsetHeight + 'px';
      };
      updateHeight();

      el.parentNode.insertBefore(spacer, el.nextSibling);
      new ResizeObserver(updateHeight).observe(el);
      window.addEventListener('resize', updateHeight);
    }

    // ─────────────────────────────────
    // TOGGLE BUTTONS (hamburguesa, búsqueda)
    // ─────────────────────────────────
    _bindToggleButtons() {
      this.root.querySelectorAll('[data-dbh-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const type = btn.dataset.dbhToggle;
          if (type === 'drawer') {
            this._drawerOpen ? this.closeDrawer() : this.openDrawer();
          } else if (type === 'search') {
            this._searchOpen ? this.closeSearch() : this.openSearch();
          }
        });
      });
    }

    // ─────────────────────────────────
    // CLOSE BUTTONS
    // ─────────────────────────────────
    _bindCloseButtons() {
      // Drawer close buttons
      this.drawerEl?.querySelectorAll('[data-dbh-close="drawer"]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDrawer());
      });

      // Search close buttons
      this.searchModal?.querySelectorAll('[data-dbh-close="search"]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeSearch());
      });
    }

    // ─────────────────────────────────
    // OVERLAYS (click fuera)
    // ─────────────────────────────────
    _bindOverlays() {
      this.drawerEl?.querySelector('.dbh-drawer__overlay')?.addEventListener('click', () => {
        this.closeDrawer();
      });

      this.searchModal?.querySelector('.dbh-search-modal__overlay')?.addEventListener('click', () => {
        this.closeSearch();
      });

      // Autocomplete listeners
      this.searchInput?.addEventListener('input', this._onSearchInput);
      this.searchInput?.addEventListener('keydown', this._onSearchKeyNav);
      this.searchClear?.addEventListener('click', () => this._clearSearch());

      // Click en resultado navega al producto
      this.searchResults?.addEventListener('click', (e) => {
        const item = e.target.closest('[data-dbh-result-url]');
        if (item) window.location.href = item.dataset.dbhResultUrl;
      });
    }

    // ─────────────────────────────────
    // SUBMENÚS DEL DRAWER
    // ─────────────────────────────────
    _bindSubmenus() {
      this.drawerEl?.querySelectorAll('.dbh-drawer__link--toggle').forEach((toggleBtn) => {
        toggleBtn.addEventListener('click', () => {
          const expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
          const submenuId = toggleBtn.getAttribute('aria-controls');
          const submenu = document.getElementById(submenuId);

          // Cerrar todos los demás submenús
          this.drawerEl.querySelectorAll('.dbh-drawer__link--toggle').forEach((otherBtn) => {
            if (otherBtn !== toggleBtn) {
              const otherId = otherBtn.getAttribute('aria-controls');
              const otherSubmenu = document.getElementById(otherId);
              otherBtn.setAttribute('aria-expanded', 'false');
              otherSubmenu?.setAttribute('hidden', '');
            }
          });

          // Toggle el actual
          if (expanded) {
            toggleBtn.setAttribute('aria-expanded', 'false');
            submenu?.setAttribute('hidden', '');
          } else {
            toggleBtn.setAttribute('aria-expanded', 'true');
            submenu?.removeAttribute('hidden');
          }
        });
      });
    }

    // ─────────────────────────────────
    // NAVEGACIÓN DESKTOP (accesibilidad teclado)
    // ─────────────────────────────────
    _bindDesktopNav() {
      const navItems = this.root.querySelectorAll('.dbh-nav__item--has-children');

      navItems.forEach((item) => {
        const link    = item.querySelector('.dbh-nav__link');
        const dropdown = item.querySelector('.dbh-nav__dropdown');

        if (!link || !dropdown) return;

        // Abrir dropdown al hacer focus en el link padre
        link.addEventListener('focus', () => {
          // Accessibility: establecer aria-expanded al abrir
          link.setAttribute('aria-expanded', 'true');
        });

        item.addEventListener('focusout', (e) => {
          // Solo cerrar si el foco sale completamente del item
          requestAnimationFrame(() => {
            if (!item.contains(document.activeElement)) {
              link.setAttribute('aria-expanded', 'false');
            }
          });
        });

        // ESC cierra el dropdown y vuelve al link padre
        dropdown.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            link.setAttribute('aria-expanded', 'false');
            link.focus();
          }
        });
      });
    }

    // ─────────────────────────────────
    // ABRIR DRAWER
    // ─────────────────────────────────
    openDrawer() {
      if (!this.drawerEl) return;

      this._lastFocusedEl = document.activeElement;
      this._drawerOpen = true;

      // Actualizar estado visual
      this.drawerEl.removeAttribute('hidden');
      this.hamburger?.setAttribute('aria-expanded', 'true');

      // Bloquear scroll
      lockScroll();

      // Foco al panel
      requestAnimationFrame(() => {
        const firstFocusable = getFocusableElements(this.drawerPanel)?.[0];
        firstFocusable?.focus();
      });

      // Escuchar keydown
      document.addEventListener('keydown', this._onKeydown);

      // Dispatch evento personalizado
      this.root.dispatchEvent(new CustomEvent('dbh:drawer:open', { bubbles: true }));
    }

    // ─────────────────────────────────
    // CERRAR DRAWER
    // ─────────────────────────────────
    closeDrawer() {
      if (!this.drawerEl || !this._drawerOpen) return;

      this._drawerOpen = false;

      // Actualizar estado visual después de la transición
      const panel = this.drawerPanel;
      const onTransitionEnd = () => {
        this.drawerEl.setAttribute('hidden', '');
        panel.removeEventListener('transitionend', onTransitionEnd);
      };
      panel.addEventListener('transitionend', onTransitionEnd);

      this.hamburger?.setAttribute('aria-expanded', 'false');

      // Restablecer submenús
      this.drawerEl.querySelectorAll('.dbh-drawer__link--toggle').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });
      this.drawerEl.querySelectorAll('.dbh-drawer__submenu').forEach((sub) => {
        sub.setAttribute('hidden', '');
      });

      // Desbloquear scroll
      unlockScroll();

      // Devolver foco
      this._clearSearch();
      this._lastFocusedEl?.focus();
      this._lastFocusedEl = null;

      // Dejar de escuchar keydown
      document.removeEventListener('keydown', this._onKeydown);

      this.root.dispatchEvent(new CustomEvent('dbh:drawer:close', { bubbles: true }));
    }

    // ─────────────────────────────────
    // ABRIR BÚSQUEDA
    // ─────────────────────────────────
    openSearch() {
      if (!this.searchModal) return;

      this._lastFocusedEl = document.activeElement;
      this._searchOpen    = true;
      this._activeIndex   = -1;

      this.searchModal.removeAttribute('hidden');

      requestAnimationFrame(() => {
        this.searchInput?.focus();
        this.searchInput?.select();
      });

      document.addEventListener('keydown', this._onKeydown);
      this.root.dispatchEvent(new CustomEvent('dbh:search:open', { bubbles: true }));
    }

    // ─────────────────────────────────
    // CERRAR BÚSQUEDA
    // ─────────────────────────────────
    closeSearch() {
      if (!this.searchModal || !this._searchOpen) return;

      this._searchOpen = false;

      // Primero quitar pointer-events para no bloquear nada mientras anima
      this.searchModal.style.pointerEvents = 'none';

      const inner = this.searchModal.querySelector('.dbh-search-modal__inner');
      const onTransitionEnd = () => {
        // Ocultar completamente con display:none (via hidden) al terminar la animación
        this.searchModal.setAttribute('hidden', '');
        this.searchModal.style.pointerEvents = '';
        inner?.removeEventListener('transitionend', onTransitionEnd);
      };
      inner?.addEventListener('transitionend', onTransitionEnd);

      // Fallback: si la transición no dispara (e.g. prefers-reduced-motion)
      setTimeout(() => {
        if (this._searchOpen === false && !this.searchModal.hasAttribute('hidden')) {
          this.searchModal.setAttribute('hidden', '');
          this.searchModal.style.pointerEvents = '';
        }
      }, 400);

      this._lastFocusedEl?.focus();
      this._lastFocusedEl = null;

      document.removeEventListener('keydown', this._onKeydown);
      this.root.dispatchEvent(new CustomEvent('dbh:search:close', { bubbles: true }));
    }

    // ─────────────────────────────────
    // MANEJADOR GLOBAL DE TECLADO
    // ─────────────────────────────────
    _handleKeydown(event) {
      // Cerrar con ESC
      if (event.key === 'Escape') {
        if (this._drawerOpen) {
          this.closeDrawer();
        } else if (this._searchOpen) {
          this.closeSearch();
        }
        return;
      }

      // Trap focus en el drawer
      if (this._drawerOpen && this.drawerPanel) {
        trapFocus(event, this.drawerPanel);
      }

      // Trap focus en el modal de búsqueda
      if (this._searchOpen && this.searchModal) {
        trapFocus(event, this.searchModal.querySelector('.dbh-search-modal__inner'));
      }
    }
  }

  // ─────────────────────────────────────────
  // INICIALIZACIÓN AL CARGAR EL DOM
  // ─────────────────────────────────────────
  function initAll() {
    document.querySelectorAll('[id^="slider-dibai-"]').forEach((root) => {
      const sectionId = root.id.replace('slider-dibai-', '');
      // Evitar doble inicialización
      if (!root._dibaiHeader) {
        root._dibaiHeader = new HeaderDibai(sectionId);
      }
    });
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ─────────────────────────────────────────
  // SHOPIFY SECTION EVENTS (Theme Editor)
  // ─────────────────────────────────────────
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[id^="slider-dibai-"]');
    if (root) {
      const sectionId = root.id.replace('slider-dibai-', '');
      root._dibaiHeader = new HeaderDibai(sectionId);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[id^="slider-dibai-"]');
    if (root?._dibaiHeader) {
      // Limpiar estado si el drawer estaba abierto
      const header = root._dibaiHeader;
      if (header._drawerOpen) header.closeDrawer();
      if (header._searchOpen) header.closeSearch();
      document.removeEventListener('keydown', header._onKeydown);
      root._dibaiHeader = null;
    }
  });

})();

/* ── Slider de barra de anuncios ── */
(function () {
  function initAnn(el) {
    const slides  = Array.from(el.querySelectorAll('[data-dbh-ann-track] .dbh-ann-slide'));
    const prev    = el.querySelector('[data-dbh-ann-prev]');
    const next    = el.querySelector('[data-dbh-ann-next]');
    const speed   = parseInt(el.dataset.dbhAnnSpeed || '4000', 10);
    if (slides.length < 2) return;

    let cur = 0;
    let timer;

    function go(idx) {
      slides[cur].classList.remove('dbh-ann-slide--active');
      slides[cur].setAttribute('aria-hidden', 'true');
      cur = ((idx % slides.length) + slides.length) % slides.length;
      slides[cur].classList.add('dbh-ann-slide--active');
      slides[cur].setAttribute('aria-hidden', 'false');
    }

    function start() { timer = setInterval(() => go(cur + 1), speed); }
    function stop()  { clearInterval(timer); }

    prev?.addEventListener('click', () => { stop(); go(cur - 1); start(); });
    next?.addEventListener('click', () => { stop(); go(cur + 1); start(); });

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);

    start();
  }

  function initAll() {
    document.querySelectorAll('[data-dbh-ann]').forEach(initAnn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => {
    e.target.querySelectorAll('[data-dbh-ann]').forEach(initAnn);
  });
})();
